<template>
    <section class="component discussion-profile-modes-page" style="position: relative; flex-grow: 1;">
        <section class="component__header">
            <section class="discussion-profile-modes-page__header-copy">
                <span class="component__header__title">
                    {{ $t("com.discussion.profile.modes") }}
                </span>
                <span class="discussion-profile-modes-page__subtitle">
                    {{ profileName }}
                </span>
            </section>
            <section class="component__header__action">
                <ButtonOutline color="light" type="sm" @click="openCreateModal" :disable="isCreateDisabled">
                    <ButtonIcon icon="plus-small" invert />
                    {{ $t("com.global.add") }}
                </ButtonOutline>
                <ButtonOutline color="light" type="sm" @click="goBack">
                    {{ $t("com.global.back") }}
                </ButtonOutline>
            </section>
        </section>
        <section class="component__body">
            <div class="table-bootstrap-responsive" v-if="hasModes">
                <table
                    class="table-bootstrap table-bootstrap-hover table-bootstrap-bordered table-bootstrap-striped align-middle">
                    <thead>
                        <tr class="align-middle">
                            <th>{{ $t("com.discussion.profile.mode-type") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-block") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-first-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-second-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-third-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-next-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-limit-time-type") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-elastic-statement") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-limit-group") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-request-limit") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-turn-off") }}</th>
                            <th>{{ $t("com.discussion.profile.mode-sound-notification") }}</th>
                            <th style="width: 20px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="row in groupedModes" :key="row.mode.id"
                            :class="{ 'discussion-profile-modes-page__grouped-row': row.depth > 0 }">
                            <td>
                                <div class="discussion-profile-modes-page__mode-cell"
                                    :style="{ paddingLeft: `${row.depth * 24}px` }">
                                    <span v-if="row.depth > 0"
                                        class="discussion-profile-modes-page__group-marker"></span>
                                    {{ row.mode.type || "-" }}
                                </div>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">{{ row.mode.isBlock ? $t("com.global.yes") :
                                $t("com.global.no") }}</td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">
                                <span
                                    :class="{ 'discussion-profile-modes-page__muted-value': isStandardStatementMuted(row.mode) }">
                                    {{ getStatementTimeLabel(row.mode.firstStatement) }}
                                </span>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">
                                <span
                                    :class="{ 'discussion-profile-modes-page__muted-value': isStandardStatementMuted(row.mode) }">
                                    {{ getStatementTimeLabel(row.mode.secondStatement) }}
                                </span>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">
                                <span
                                    :class="{ 'discussion-profile-modes-page__muted-value': isStandardStatementMuted(row.mode) }">
                                    {{ getStatementTimeLabel(row.mode.thirdStatement) }}
                                </span>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">
                                <span
                                    :class="{ 'discussion-profile-modes-page__muted-value': isStandardStatementMuted(row.mode) }">
                                    {{ getStatementTimeLabel(row.mode.nextStatement) }}
                                </span>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">{{ getLimitTimeTypeLabel(row.mode.limitTimeType) }}</td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">
                                <span
                                    :class="{ 'discussion-profile-modes-page__muted-value': isElasticRequestMuted(row.mode) }">
                                    {{ getElasticStatementLabel(row.mode) }}
                                </span>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">
                                <span
                                    :class="{ 'discussion-profile-modes-page__muted-value': isElasticRequestMuted(row.mode) }">
                                    {{ getRequestLimitLabel(row.mode.requestLimitId) }}
                                </span>
                            </td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">{{ row.mode.turnOff ? $t("com.global.yes") :
                                $t("com.global.no") }}</td>
                            <td v-if="!row.isInheritedGroup" class="align-middle text-center"
                                :rowspan="row.optionRowspan">{{ row.mode.isSoundNotification ? $t("com.global.yes") :
                                $t("com.global.no") }}</td>
                            <td>
                                <ButtonGroup>
                                    <ButtonOutline color="light" type="sm" @click="openEditModal(row.mode)">
                                        <ButtonIcon icon="edit" />
                                        {{ $t("com.global.edit") }}
                                    </ButtonOutline>
                                    <ButtonOutline color="danger" type="sm" v-if="!modeHasChildren(row.mode)" @click="deleteMode(row.mode)">
                                        <ButtonIcon icon="delete" />
                                        {{ $t("com.global.delete") }}
                                    </ButtonOutline>
                                </ButtonGroup>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <section v-else-if="!isLoading" class="text-center">
                {{ $t("com.global.no-data") }}
            </section>
        </section>

        <SmallLoader v-if="isLoading || isSaving" />
        <AddDiscussionProfileModeModal v-if="isShowCreateModeModal" :discussion-id="discussionId"
            :statement-types="statementTypes" :statement-time-options="statementTimeOptions"
            :limit-time-type-options="limitTimeTypeOptions" :request-limit-types="requestLimitTypes"
            :available-limit-group-modes="modes" :used-type-ids="usedTypeIds" @close="closeCreateModal"
            @created="handleModeCreated" />
        <EditDiscussionProfileModeModal v-if="isShowEditModeModal && selectedMode" :discussion-id="discussionId"
            :mode="selectedMode" :statement-types="statementTypes" :statement-time-options="statementTimeOptions"
            :limit-time-type-options="limitTimeTypeOptions" :request-limit-types="requestLimitTypes"
            :available-limit-group-modes="modes" :used-type-ids="usedTypeIdsForEdit" @close="closeEditModal"
            @updated="handleModeUpdated" />
    </section>
</template>

<script charset="utf-8">
import AddDiscussionProfileModeModal from "@/components/Panel/Content/DiscussionProfile/Modals/AddDiscussionProfileModeModal.vue";
import EditDiscussionProfileModeModal from "@/components/Panel/Content/DiscussionProfile/Modals/EditDiscussionProfileModeModal.vue";
import SmallLoader from "@/components/BasicControls/SmallLoader.vue";
import AlertHelper from "@/helpers/alerts.helper.js";
import DiscussionProfileService from "@/services/discussion.profile.service";

export default {
    name: "DiscussionProfileModesPage",
    components: {
        AddDiscussionProfileModeModal,
        EditDiscussionProfileModeModal,
        SmallLoader
    },
    data() {
        return {
            isLoading: false,
            isSaving: false,
            isShowCreateModeModal: false,
            isShowEditModeModal: false,
            modes: [],
            profile: null,
            selectedMode: null,
            statementTypes: [],
            requestLimitTypes: [],
            limitTimeTypeOptions: [
                { id: 1, textKey: "com.discussion.profile.mode-limit-time-type.fixed", fallback: "Fixed" },
                { id: 2, textKey: "com.discussion.profile.mode-limit-time-type.flex", fallback: "Flexible" }
            ],
            statementTimeOptions: [
                { textKey: "com.discussion.profile.mode-time.unlimited", id: -1 },
                { textKey: "com.discussion.profile.mode-time.blocked", id: -2 },
                { textKey: "com.discussion.profile.mode-time.10-seconds", id: 10 },
                { textKey: "com.discussion.profile.mode-time.20-seconds", id: 20 },
                { textKey: "com.discussion.profile.mode-time.30-seconds", id: 30 },
                { textKey: "com.discussion.profile.mode-time.40-seconds", id: 40 },
                { textKey: "com.discussion.profile.mode-time.50-seconds", id: 50 },
                { textKey: "com.discussion.profile.mode-time.1-minute", id: 60 },
                { textKey: "com.discussion.profile.mode-time.1-5-minutes", id: 90 },
                { textKey: "com.discussion.profile.mode-time.2-minutes", id: 120 },
                { textKey: "com.discussion.profile.mode-time.3-minutes", id: 180 },
                { textKey: "com.discussion.profile.mode-time.4-minutes", id: 240 },
                { textKey: "com.discussion.profile.mode-time.5-minutes", id: 300 },
                { textKey: "com.discussion.profile.mode-time.6-minutes", id: 360 },
                { textKey: "com.discussion.profile.mode-time.7-minutes", id: 420 },
                { textKey: "com.discussion.profile.mode-time.8-minutes", id: 480 },
                { textKey: "com.discussion.profile.mode-time.9-minutes", id: 540 },
                { textKey: "com.discussion.profile.mode-time.10-minutes", id: 600 },
                { textKey: "com.discussion.profile.mode-time.15-minutes", id: 900 },
                { textKey: "com.discussion.profile.mode-time.20-minutes", id: 1200 },
                { textKey: "com.discussion.profile.mode-time.25-minutes", id: 1500 },
                { textKey: "com.discussion.profile.mode-time.30-minutes", id: 1800 },
                { textKey: "com.discussion.profile.mode-time.40-minutes", id: 2400 },
                { textKey: "com.discussion.profile.mode-time.45-minutes", id: 2700 },
                { textKey: "com.discussion.profile.mode-time.50-minutes", id: 3000 },
                { textKey: "com.discussion.profile.mode-time.1-hour", id: 3600 }
            ]
        };
    },
    computed: {
        discussionId() {
            return Number(this.$route.params.discussionId);
        },
        hasModes() {
            return Array.isArray(this.modes) && this.modes.length > 0;
        },
        profileName() {
            return this.profile?.name || this.$t("com.discussion.discussion-profile");
        },
        usedTypeIds() {
            return this.modes
                .map((mode) => Number(mode.typeId))
                .filter((typeId) => Number.isFinite(typeId));
        },
        usedTypeIdsForEdit() {
            if (!this.selectedMode?.id) {
                return this.usedTypeIds;
            }

            return this.modes
                .filter((mode) => mode.id !== this.selectedMode.id)
                .map((mode) => Number(mode.typeId))
                .filter((typeId) => Number.isFinite(typeId));
        },
        groupedModes() {
            const modesById = new Map(this.modes.map((mode) => [Number(mode.id), mode]));
            const childModesByParentId = new Map();

            this.modes.forEach((mode) => {
                const parentId = this.getGroupedParentId(mode, modesById);

                if (parentId === null) {
                    return;
                }

                const groupedModes = childModesByParentId.get(parentId) ?? [];
                groupedModes.push(mode);
                childModesByParentId.set(parentId, groupedModes);
            });

            const flattenedModes = [];
            const visitedModeIds = new Set();
            const countSubtreeRows = (modeId, lineage = new Set()) => {
                if (lineage.has(modeId)) {
                    return 0;
                }

                const nextLineage = new Set(lineage);
                nextLineage.add(modeId);

                const groupedChildren = childModesByParentId.get(modeId) ?? [];

                return 1 + groupedChildren.reduce((total, childMode) => {
                    return total + countSubtreeRows(Number(childMode.id), nextLineage);
                }, 0);
            };

            const appendMode = (mode, depth, lineage = new Set()) => {
                const modeId = Number(mode.id);
                const parentId = this.getGroupedParentId(mode, modesById);

                if (visitedModeIds.has(modeId)) {
                    return;
                }

                visitedModeIds.add(modeId);
                flattenedModes.push({
                    mode,
                    depth,
                    isInheritedGroup: parentId !== null,
                    optionRowspan: parentId === null ? countSubtreeRows(modeId) : 0
                });

                const nextLineage = new Set(lineage);
                nextLineage.add(modeId);

                const groupedChildren = childModesByParentId.get(modeId) ?? [];
                groupedChildren.forEach((childMode) => {
                    const childModeId = Number(childMode.id);

                    if (nextLineage.has(childModeId)) {
                        return;
                    }

                    appendMode(childMode, depth + 1, nextLineage);
                });
            };

            this.modes.forEach((mode) => {
                if (this.getGroupedParentId(mode, modesById) === null) {
                    appendMode(mode, 0);
                }
            });

            this.modes.forEach((mode) => {
                if (!visitedModeIds.has(Number(mode.id))) {
                    appendMode(mode, 0);
                }
            });

            return flattenedModes;
        },
        isCreateDisabled() {
            return this.isLoading || this.statementTypes.length === 0 || this.usedTypeIds.length >= this.statementTypes.length;
        }
    },
    async mounted() {
        await this.loadPage();
    },
    methods: {
        async loadPage() {
            if (!Number.isFinite(this.discussionId) || this.discussionId <= 0) {
                return;
            }

            try {
                this.isLoading = true;

                const [profileResponse, modesResponse, statementTypesResponse, requestLimitTypesResponse] = await Promise.all([
                    DiscussionProfileService.GetById(this.discussionId),
                    DiscussionProfileService.GetModes(this.discussionId),
                    DiscussionProfileService.GetStatementTypes(),
                    DiscussionProfileService.GetRequestLimtTypes()
                ]);

                this.profile = profileResponse.data ?? null;
                this.modes = this.resolveModes(profileResponse.data, modesResponse.data);
                this.statementTypes = Array.isArray(statementTypesResponse.data) ? statementTypesResponse.data : [];
                this.requestLimitTypes = Array.isArray(requestLimitTypesResponse.data) ? requestLimitTypesResponse.data : [];

                if (this.profile) {
                    this.$store.commit("setSelectedDiscussionProfile", this.profile);
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.isLoading = false;
            }
        },
        getStatementTimeLabel(value) {
            const option = this.statementTimeOptions.find((item) => item.id === value);
            return option ? this.$t(option.textKey) : value;
        },
        getLimitTimeTypeLabel(value) {
            const option = this.limitTimeTypeOptions.find((item) => item.id === Number(value));

            return option
                ? this.$t(option.textKey)
                : this.formatOptionalValue(value);
        },
        getElasticStatementLabel(mode) {
            if (Number(mode?.limitTimeType) !== 2) {
                return "-";
            }

            return this.getStatementTimeLabel(mode.elasticStatement);
        },
        isStandardStatementMuted(mode) {
            return Number(mode?.limitTimeType) === 2;
        },
        isElasticRequestMuted(mode) {
            return Number(mode?.limitTimeType) === 1;
        },
        getRequestLimitLabel(requestLimitId) {
            if (requestLimitId === null || requestLimitId === undefined || requestLimitId === "") {
                return "-";
            }

            const option = this.requestLimitTypes.find((item) => Number(item.id) === Number(requestLimitId));

            if (!option) {
                return requestLimitId;
            }

            return this.$t(option.name);
        },
        getLimitGroupLabel(mode) {
            const limitGroupId = mode?.limitGroupId;

            if (limitGroupId === null || limitGroupId === undefined || limitGroupId === "") {
                return "-";
            }

            if (Number(limitGroupId) === Number(mode?.id)) {
                return this.$t("com.global.default");
            }

            const option = this.modes.find((item) => Number(item.id) === Number(limitGroupId));

            if (!option) {
                return limitGroupId;
            }

            return option.type || limitGroupId;
        },
        getGroupedParentId(mode, modesById = new Map(this.modes.map((item) => [Number(item.id), item]))) {
            const modeId = Number(mode?.id);
            const limitGroupId = this.normalizeNullableNumber(mode?.limitGroupId);

            if (limitGroupId === null || limitGroupId === modeId) {
                return null;
            }

            return modesById.has(limitGroupId) ? limitGroupId : null;
        },
        modeHasChildren(mode) {
            const modeId = Number(mode?.id);

            if (!modeId) {
                return false;
            }

            return this.modes.some((item) => {
                return Number(item?.id) !== modeId
                    && this.normalizeNullableNumber(item?.limitGroupId) === modeId;
            });
        },
        formatOptionalValue(value) {
            return value === null || value === undefined || value === "" ? "-" : value;
        },
        normalizeNumber(value, fallback = 0) {
            const parsed = Number(value);

            return Number.isFinite(parsed) ? parsed : fallback;
        },
        normalizeNullableNumber(value) {
            if (value === null || value === undefined || value === "") {
                return null;
            }

            const parsed = Number(value);

            return Number.isFinite(parsed) ? parsed : null;
        },
        normalizeMode(mode) {
            return {
                ...mode,
                typeId: this.normalizeNumber(mode?.typeId),
                isBlock: !!mode?.isBlock,
                firstStatement: this.normalizeNumber(mode?.firstStatement),
                secondStatement: this.normalizeNumber(mode?.secondStatement),
                thirdStatement: this.normalizeNumber(mode?.thirdStatement),
                nextStatement: this.normalizeNumber(mode?.nextStatement),
                turnOff: !!mode?.turnOff,
                isSoundNotification: !!mode?.isSoundNotification,
                limitTimeType: this.normalizeNumber(mode?.limitTimeType, 1),
                limitGroupId: this.normalizeNullableNumber(mode?.limitGroupId),
                elasticStatement: this.normalizeNumber(mode?.elasticStatement, 60),
                requestLimitId: this.normalizeNullableNumber(mode?.requestLimitId)
            };
        },
        resolveModes(profileData, modesData) {
            const source = Array.isArray(profileData?.limits)
                ? profileData.limits
                : Array.isArray(modesData)
                    ? modesData
                    : [];

            return source.map((mode) => this.normalizeMode(mode));
        },
        openCreateModal() {
            if (this.isCreateDisabled) {
                return;
            }

            this.isShowCreateModeModal = true;
        },
        closeCreateModal() {
            this.isShowCreateModeModal = false;
        },
        async handleModeCreated() {
            await this.loadPage();
        },
        openEditModal(mode) {
            this.selectedMode = this.normalizeMode(mode);
            this.isShowEditModeModal = true;
        },
        closeEditModal() {
            this.isShowEditModeModal = false;
            this.selectedMode = null;
        },
        async handleModeUpdated() {
            this.closeEditModal();
            await this.loadPage();
        },
        alertInfo() {
            let info = "";

            info += `<p>${this.$t("com.global.confirmation-alert")}</p>`;
            info += `<p>${this.$t("com.discussion.profile.remove-mode-confirmation")}</p>`;

            return info;
        },
        createAlertData() {
            return {
                mainText: this.alertInfo(),
                buttons: [
                    {
                        id: 1,
                        text: this.$t("com.global.yes"),
                        color: "#d80418"
                    }
                ]
            };
        },
        async deleteMode(mode) {
            const actionToFire = async () => {
                if (!mode?.id) {
                    return;
                }

                try {
                    this.isSaving = true;
                    await DiscussionProfileService.DeleteMode(this.discussionId, mode.id);
                    this.closeEditModal();
                    await this.loadPage();
                } catch (e) {
                    if (e.response !== undefined) {
                        const t = this.$swal.mixin(AlertHelper.errorToastElement());
                        t.fire(
                            AlertHelper.errorToastAlert(
                                this.$t("com.global.default-save-error"),
                                this.$t("com.global.error")
                            )
                        );
                    }
                } finally {
                    this.isSaving = false;
                }
            };

            this.$store.commit("setAlertData", this.createAlertData());
            this.$store.commit("showConfirmationAlertWithData", actionToFire);
        },
        goBack() {
            this.$router.push({
                name: "client-settings-discussion-profile"
            });
        }
    }
};
</script>

<style lang="scss" scoped>
@use "@/assets/scss/user-profile-component.scss";

.discussion-profile-modes-page {
    &__header-copy {
        display: flex;
        flex-direction: column;
        row-gap: 4px;
    }

    &__subtitle {
        color: #808396;
        font-size: 14px;
        line-height: 1.4;
    }

    .component__header__action {
        display: flex;
        flex-direction: row;
        column-gap: 12px;
    }

    .component__body {
        min-height: 0;
    }

    &__mode-cell {
        display: flex;
        align-items: center;
        column-gap: 8px;
    }

    &__group-marker {
        width: 12px;
        height: 1px;
        background: #808396;
        flex-shrink: 0;
    }

    &__grouped-row {
        background: rgba(128, 131, 150, 0.06);
    }

    &__muted-value {
        color: #8d93a5;
        opacity: 0.65;
        text-decoration-line: line-through;
        text-decoration-style: dotted;
        text-decoration-thickness: 1px;
    }
}
</style>
